import { IRasterLayer } from '../../interfaces/sjmap2/raster/IRaster';
import { IRasterSource } from '../../interfaces/sjmap2/raster/IRasterSource';
import { ISJMap2 } from '../../interfaces/sjmap2/ISJMap2';
import type { BiomapStrategicChangeProduct } from '../../interfaces/strategic-view/IBiomapStrategicChange';
import { getBiomapRasterIdentity } from '../../utils/biomap/biomap-layer.utils';

export type StrategicDateSlot = 'A' | 'B';
export type StrategicMapSlot = StrategicDateSlot | 'COMPARE';

export interface StrategicSourceSelectionChange {
  slot: StrategicDateSlot;
  source: IRasterSource;
}

export type RasterBbox = [number, number, number, number];

export interface StrategicComparisonSources {
  sourceA: IRasterSource | null;
  sourceB: IRasterSource | null;
}

export function isStrategicRasterLayer(layer: unknown): layer is IRasterLayer {
  return !!layer && (layer as IRasterLayer).type === 'raster';
}

const TEMPORARY_STRATEGIC_SUHI_NAME_KEYWORDS = [
  'SUHI',
  'SURFACE URBAN HEAT ISLAND',
  'UHI',
  'TCD',
  'TREE COVER DENSITY',
  'LST',
  'LAND SURFACE TEMPERATURE',
  // 'GREEN HEALTH',
  // 'HPR',
  // 'HEATWAVE POTENTIAL RISK',
  'CARBON STORAGE'
] as const;

export function isStrategicSuhiRasterLayer(
  layer: Pick<IRasterLayer, 'name'>,
): boolean {
  const normalizedName = (layer.name ?? '').toUpperCase();

  return TEMPORARY_STRATEGIC_SUHI_NAME_KEYWORDS.some((keyword) =>
    normalizedName.includes(keyword),
  );
}


export function getStrategicBiomapProduct(
  layer: IRasterLayer | null | undefined,
): BiomapStrategicChangeProduct | null {
  if (!layer) {
    return null;
  }

  const identity = getBiomapRasterIdentity(layer);

  if (!identity) {
    return null;
  }

  if (identity.product === 'msa' && identity.msaComponent !== 'overall') {
    return null;
  }

  return identity.product;
}

export function getStrategicRasterLayers(
  sjmap: ISJMap2 | null | undefined,
): IRasterLayer[] {
  const simulatedGroupUUIDs = new Set(
    (sjmap?.groups ?? [])
      .filter((group) => group.is_simulated)
      .map((group) => group.uuid),
  );

  return (sjmap?.layers ?? [])
    .filter(isStrategicRasterLayer)
    .filter(
      (layer) =>
        isStrategicSuhiRasterLayer(layer) ||
        getStrategicBiomapProduct(layer) !== null,
    )
    .filter(
      (layer) =>
        !layer.group_id || !simulatedGroupUUIDs.has(layer.group_id),
    )
    .filter((layer) => !!layer.sources?.length);
}

export function getStrategicSuhiRasterLayers(
  sjmap: ISJMap2 | null | undefined,
): IRasterLayer[] {
  const simulatedGroupUUIDs = new Set(
    (sjmap?.groups ?? [])
      .filter((group) => group.is_simulated)
      .map((group) => group.uuid),
  );

  return (sjmap?.layers ?? [])
    .filter(isStrategicRasterLayer)
    .filter(isStrategicSuhiRasterLayer)
    .filter(
      (layer) =>
        !layer.group_id || !simulatedGroupUUIDs.has(layer.group_id),
    )
    .filter((layer) => !!layer.sources?.length);
}

export function getRasterSourceUUID(source: IRasterSource | null | undefined): string {
  if (!source) {
    return '';
  }

  const sourceWithIdentity = source as IRasterSource & { id?: string };
  return sourceWithIdentity.uuid ?? sourceWithIdentity.id ?? '';
}

export function getRasterSourceKey(source: IRasterSource | null | undefined): string {
  if (!source) {
    return '';
  }

  return [
    getRasterSourceUUID(source),
    source.source ?? '',
    source.reference_date ?? '',
    source.start_date ?? '',
    source.end_date ?? '',
  ].join('|');
}

export function formatStrategicDate(
  rawDate: string | null | undefined,
  formatterDate: string | null | undefined,
): string {
  if (!rawDate) {
    return 'No date';
  }

  const dateOnly = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate;
  const [year, month, day] = dateOnly.split('-');

  if (formatterDate === 'yyyy' && year) {
    return year;
  }

  if ((formatterDate === 'yyyy-MM' || formatterDate === 'yyyy/MM') && year && month) {
    return `${year}-${month}`;
  }

  if (
    (formatterDate === 'yyyy-MM-dd' || formatterDate === 'yyyy/MM/dd' || formatterDate === 'dd/MM/yyyy') &&
    year &&
    month &&
    day
  ) {
    return formatterDate === 'dd/MM/yyyy' ? `${day}/${month}/${year}` : `${year}-${month}-${day}`;
  }

  return dateOnly;
}

export function formatRasterSourceDate(source: IRasterSource | null | undefined): string {
  if (!source) {
    return '—';
  }

  const rawDate = source.reference_date || source.start_date || source.end_date || '';
  return formatStrategicDate(rawDate, source.formatter_date);
}

export function sortRasterSourcesByDate(sources: IRasterSource[] = []): IRasterSource[] {
  return [...sources].sort((left, right) => {
    const leftDate = getComparableSourceDate(left);
    const rightDate = getComparableSourceDate(right);

    return leftDate.localeCompare(rightDate);
  });
}

export function orderStrategicComparisonSources(
  sourceA: IRasterSource | null,
  sourceB: IRasterSource | null,
): StrategicComparisonSources {
  if (!sourceA || !sourceB) {
    return { sourceA, sourceB };
  }

  const [olderSource, newerSource] = sortRasterSourcesByDate([sourceA, sourceB]);

  return {
    sourceA: olderSource,
    sourceB: newerSource,
  };
}

export function getRasterLayerBbox(layer: IRasterLayer | null | undefined): RasterBbox | null {
  const coordinates = layer?.bounds?.coordinates?.[0];

  if (!coordinates?.length) {
    return null;
  }

  const bbox: RasterBbox = [Infinity, Infinity, -Infinity, -Infinity];

  coordinates.forEach((coordinate: number[]) => {
    bbox[0] = Math.min(coordinate[0], bbox[0]);
    bbox[1] = Math.min(coordinate[1], bbox[1]);
    bbox[2] = Math.max(coordinate[0], bbox[2]);
    bbox[3] = Math.max(coordinate[1], bbox[3]);
  });

  return bbox.every(Number.isFinite) ? bbox : null;
}

function getComparableSourceDate(source: IRasterSource): string {
  return (
    source.reference_date ||
    source.start_date ||
    source.end_date ||
    source.formatter_date ||
    ''
  );
}
